import { CompositeFilterDescriptor, DataSourceRequestState, FilterDescriptor, State as GridState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import { Button } from '@progress/kendo-react-buttons';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridColumn as Column, Grid, GridCustomCellProps, GridCustomHeaderCellProps, GridEvent, GridHandle, GridItemChangeEvent, GridRowDoubleClickEvent, GridToolbar } from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { NumericTextBox, NumericTextBoxChangeEvent, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { filterClearIcon } from '@progress/kendo-svg-icons';
import { JsonResponse } from 'TypeGen/json-response';
import { RightAlignHeaderCell } from 'components/cells/RightAlignHeaderCell';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { debounce } from 'ts-debounce';
import CenterDivPanel from '../../components/CenterDivPanel';
import KendoWindow from '../../components/KendoWindow';
import useAlert from '../../components/useAlert';
import useConfirm from '../../components/useConfirm';
import { fetchApi } from '../../services/api';
import { IDName } from '../../types/idname';
import { Title } from '../../utils/title';
import DriverAutoComplete from '../AssetDrivers/DriverAutoComplete';
import OwnerAutoComplete from '../AssetOwners/OwnerAutoComplete';
import VehicleAutoComplete from '../AssetVehicles/VehicleAutoComplete';
import { DeductionType } from './DeductionCOAs';
import PayPeriodDropDownList from './PayPeriodDropDownList';
import { DeductionScheduleType, deductionScheduleTypeData } from './ScheduledDeductions';

type Props = {
  payDeductionID?: number;
  onDialogClose?: () => void;
};

enum DeductionLineItemStatusType { Pending = 115, Completed = 120, Cancelled = 130 };
const statusData = [{ ID: DeductionLineItemStatusType.Pending, Name: DeductionLineItemStatusType[DeductionLineItemStatusType.Pending] },
{ ID: DeductionLineItemStatusType.Completed, Name: DeductionLineItemStatusType[DeductionLineItemStatusType.Completed] },
{ ID: DeductionLineItemStatusType.Cancelled, Name: DeductionLineItemStatusType[DeductionLineItemStatusType.Cancelled] }] as IDName[];

export interface PendingDeduction {
  PayDeductionLineItemID: number;
  BusinessUnitID: number;

  DriverID: number;
  DriverNumber: string;
  VehicleID: number;
  VehicleNumber: string;
  DeductionLineItemOwnerID: number;
  DeductionLineItemOwnerNumber: string;
  ScheduleType: number;
  ScheduleTypeName: string;
  FirstPeriodID: number;
  //FirstPayPeriodName: string;
  DeductionTypeID: number;
  DeductionTypeName: string;
  Status: number;
  StatusName: string;

  PayDeductionID: number;
  Comments: string

  TotalAmount: number;
  AmortizeOver: number;
  AmountPerPay: number;

  Paid: number;
  Balance: number;
  PaymentsRemaining: number;

  DeductionLineItemAmount: number;
  DeductionLineItemComment: string;
  DeductionLineItemSuperceded: boolean;
  DeductionLineItemPayPeriodID: number;
  DeductionLineItemPayPeriodName: string;
  DeductionLineItemPayStatus: DeductionLineItemStatusType;
  DeductionLineItemPayPeriodClosed: boolean;
  DeductionLineItemPayStatusName: string;

  inEdit?: boolean | string;
  isNew?: boolean;
  Delete?: boolean;
}

const PendingDeductionsContext = createContext<{
  DeductionTypes: DeductionType[],
}>({
  DeductionTypes: []
})

interface EditCommandCellProps extends GridCustomCellProps {
  edit: (item: PendingDeduction) => void;
  remove: (item: PendingDeduction) => void;
  add: (item: PendingDeduction) => void;
  discard: () => void;
  update: (item: PendingDeduction) => void;
  cancel: (item: PendingDeduction) => void;
  editField: string;
}

const PayDeductionLineItemIDCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const dataItem: PendingDeduction = props.dataItem;
  //var value = props.dataItem[props.field] as string;

  return (
    <td {...props.tdProps} style={{ textAlign: "left" }}>
      {props.dataItem.isNew === true ? <span>-</span> : <div>{dataItem.PayDeductionLineItemID}<br />{dataItem.PayDeductionID}</div>}
    </td>
  );
}

const AssetCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const dataItem: PendingDeduction = props.dataItem;

  const handleChange = (resourceID: number | null, field: string) => {
    if (props.onChange) {

      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: field, //props.field,
        syntheticEvent: null,
        value: resourceID ?? 0,
      });
    }
  };

  return (
    <td {...props.tdProps}>
      {dataItem.isNew ? <DriverAutoComplete
        selectedDriverID={dataItem.DriverID}
        onSelectedDriver={(e) => handleChange(e?.DriverID, "DriverID")}
      /> :
        <div>{dataItem.DriverNumber}</div>
      }

      {dataItem.isNew ? <VehicleAutoComplete
        selectedVehicleID={dataItem.VehicleID}
        onSelectedVehicle={(e) => handleChange(e?.VehicleID, "VehicleID")}
      /> :
        <div>{dataItem.VehicleNumber}</div>
      }

      {dataItem.isNew ? <OwnerAutoComplete
        selectedOwnerID={dataItem.DeductionLineItemOwnerID}
        onSelectedOwner={(e) => handleChange(e?.OwnerID, "DeductionLineItemOwnerID")}
      /> :
        <div>{dataItem.DeductionLineItemOwnerNumber}</div>
      }
    </td>
  );
};

const AssetHeaderCell = (props: GridCustomHeaderCellProps) => {
  return (
    <HeaderThElement {...props.thProps}>
      <div>Driver ID</div>
      <div>Vehicle ID</div>
    </HeaderThElement>
  );
};

const ScheduleHeaderCell = (props: GridCustomHeaderCellProps) => {
  return (
    <HeaderThElement {...props.thProps}>
      <div>Deduction Type</div>
      <div>Schedule</div>
      <div>Starting Pay Period</div>
    </HeaderThElement>
  );
};

const ScheduleCell = (props: GridCustomCellProps) => {
  const context = useContext(PendingDeductionsContext);

  if (!props.field)
    return null;

  const dataItem: PendingDeduction = props.dataItem;

  const handleChange = (e: DropDownListChangeEvent, value: number, field: string) => {
    if (props.onChange) {

      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: field, //props.field,
        syntheticEvent: e.syntheticEvent,
        value: value,
      });
    }
  };

  const handleChangePayPeriod = (value: number, field: string) => {
    if (props.onChange) {

      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: field, //props.field,
        syntheticEvent: undefined,
        value: value,
      });
    }
  };

  return (
    <td {...props.tdProps}>
      {dataItem.isNew ? <DropDownList
        className="my-1"
        data={context.DeductionTypes}
        textField="Name"
        dataItemKey="PayDeductionTypeID"
        value={context.DeductionTypes.find((x: DeductionType) => x.PayDeductionTypeID == dataItem.DeductionTypeID)}
        onChange={(e) => handleChange(e, e.target.value.PayDeductionTypeID, 'DeductionTypeID')}
      /> :
        <div>{dataItem.DeductionTypeName}</div>
      }

      {dataItem.isNew ? <DropDownList
        className="my-1"
        data={deductionScheduleTypeData}
        textField="Name"
        dataItemKey="ID"
        value={deductionScheduleTypeData.find((x: IDName) => x.ID == dataItem.ScheduleType)}
        onChange={(e) => handleChange(e, e.target.value.ID, 'ScheduleType')}
      /> :
        <div>{dataItem.ScheduleTypeName}</div>
      }

      {dataItem.isNew ? <PayPeriodDropDownList
        className="my-1"
        onSelected={(e) => {
          handleChangePayPeriod(e.PayPeriodID, 'DeductionLineItemPayPeriodID');
        }}
        selectedPayPeriodID={dataItem.DeductionLineItemPayPeriodID}
        includeClosedPayPeriods={false}
        includeOpenPayPeriods={true}
      /> :
        <div>{dataItem.DeductionLineItemPayPeriodName}</div>
      }

    </td>
  );
};

const StatusCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const dataItem: PendingDeduction = props.dataItem;

  const handleChange = (e: DropDownListChangeEvent, value: number, field: string) => {
    if (props.onChange) {

      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: field,
        syntheticEvent: e.syntheticEvent,
        value: value,
      });
    }
  };

  const canEdit = dataItem.inEdit && dataItem.DeductionLineItemPayStatus !== DeductionLineItemStatusType.Completed && dataItem.DeductionLineItemPayPeriodClosed == false;

  return (
    <td {...props.tdProps}>
      {canEdit ? <DropDownList
        className="my-1"
        data={statusData.filter(x => x.ID !== DeductionLineItemStatusType.Completed)}
        textField="Name"
        dataItemKey="ID"
        value={statusData.find((x: IDName) => x.ID == dataItem.DeductionLineItemPayStatus)}
        onChange={(e) => handleChange(e, e.target.value.ID, 'DeductionLineItemPayStatus')}
      /> :
        <div>{DeductionLineItemStatusType[dataItem.DeductionLineItemPayStatus]}</div>
      }
    </td>
  );
};


const AmountCell = (props: GridCustomCellProps) => {
  //(e: DropDownListChangeEvent, value: number, field: string) => {
  const handleChange = (e: NumericTextBoxChangeEvent, field: string) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: field,
        syntheticEvent: e.syntheticEvent,
        value: e.value
      });
    }
  };

  const dataItem: PendingDeduction = props.dataItem;

  const canEdit = dataItem.inEdit && dataItem.DeductionLineItemPayStatus !== DeductionLineItemStatusType.Completed
    && dataItem.DeductionLineItemPayPeriodClosed == false && dataItem.ScheduleType == DeductionScheduleType.Amortized;

  return (
    <td {...props.tdProps} style={{ textAlign: "right" }}>
      <div>
        {canEdit || dataItem.isNew ? <NumericTextBox format="n2" value={dataItem.DeductionLineItemAmount} onChange={(e) => handleChange(e, 'DeductionLineItemAmount')} inputStyle={{ textAlign: "right" }}
          placeholder={`Total Amount`} /> :
          <span>{formatNumber(dataItem.DeductionLineItemAmount, "c2")}</span>
        }
      </div>
    </td>
  );
};

export const CommentsCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const handleChange = (e: TextBoxChangeEvent) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value,
      });
    }
  };

  //var value = props.dataItem[props.field] as string;
  const dataItem: PendingDeduction = props.dataItem;

  return (
    <td {...props.tdProps}>
      {dataItem.inEdit ? <div>
        <div><TextBox
          maxLength={30}
          value={dataItem.DeductionLineItemComment}
          onChange={handleChange}
          style={{ width: '100%' }}
        /></div>
        <div>{dataItem.Comments}</div>
      </div> :
        <div>
          <div>{dataItem.DeductionLineItemComment}</div>
          <div>{dataItem.Comments}</div>
        </div>
      }
    </td>
  );
};

const PageSize = 50;

const PendingDeductions = (props: Props) => {
  const render = useRef(true);
  const _requestHash = useRef('');

  const location = useLocation();
  const getQueryParams = (query: string): URLSearchParams => {
    return new URLSearchParams(query);
  };
  const queryParams = getQueryParams(location.search.toLowerCase());
  const payPeriodId = queryParams.has('payperiodid') ? Number(queryParams.get('payperiodid')) : 0;
  const payDeductionId = props.onDialogClose && props.payDeductionID > 0 ? props.payDeductionID : queryParams.has('paydeductionid') ? Number(queryParams.get('paydeductionid')) : null;

  //const SESSION_STORAGE_NAME = "PendingDeductionsFilterV1";

  const resetDataState = {
    skip: 0,
    take: PageSize,
    sort: [{
      field: "PayDeductionLineItemID", //PayDeductionLineItemID
      dir: "desc"
    }]
  } as GridState;

  const defaultAndFilter = {
    logic: 'and', filters: [
      { field: 'BusinessUnitID', operator: 'eq', value: 2610 }
      //{ field: 'DeductionLineItemPayStatus', operator: 'eq', value: 115 }
    ]
  } as CompositeFilterDescriptor;

  if (payPeriodId > 0)
    defaultAndFilter.filters.push({ field: 'DeductionLineItemPayPeriodID', operator: 'eq', value: payPeriodId });
  if (payDeductionId) {
    defaultAndFilter.filters.push({ field: 'PayDeductionID', operator: 'eq', value: payDeductionId });
    resetDataState.sort = [{
      field: "DeductionLineItemPayPeriodID",
      dir: "desc"
    }];
  }



  //const sessionStorageAndFilter = sessionStorage.getItem(SESSION_STORAGE_NAME);
  //const sessionStorageOwnerFilter = sessionStorage.getItem("SchedDeductions-ownerFilter");
  let andFilter: CompositeFilterDescriptor = null;
  //if (sessionStorageAndFilter) {
  //  andFilter = JSON.parse(sessionStorageAndFilter);
  //} else {
  andFilter = { ...defaultAndFilter };
  //}

  const [dataState, setDataState] = useState<DataSourceRequestState>({ ...resetDataState, filter: andFilter });
  const gridRef = useRef<GridHandle>(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const { alert } = useAlert();
  const { ConfirmationDialog, confirm } = useConfirm({});
  const [loading, setLoading] = useState(true);
  const editField: string = "inEdit";
  const [scheduledDeductions, setPendingDeductions] = useState<PendingDeduction[]>([]);
  const [CSIDataCopy, setCSIDataCopy] = useState<PendingDeduction[]>([]);
  const [deductionTypes, setDeductionTypes] = useState<DeductionType[]>([]);

  const [filterByDriverId, setFilterByDriverId] = useState(0);
  const [filterByVehicleId, setFilterByVehicleId] = useState(0);
  const [filterByOwnerId, setFilterByOwnerId] = useState(0);

  const [filterByStatus, setFilterByStatus] = useState<DeductionLineItemStatusType | number>(0);
  const [filterBySchedule, setFilterBySchedule] = useState(0);
  const [filterByDeductionType, setFilterByDeductionType] = useState(0);
  const [filterByPayPeriod, setFilterByPayPeriod] = useState(payPeriodId);
  const [filterByPayDeductionId, setFilterByPayDeductionId] = useState<number | null>(payDeductionId);

  const EditCommandCell = (_props: EditCommandCellProps) => {
    const inEdit = _props.dataItem[_props.editField];
    const dataItem: PendingDeduction = _props.dataItem;
    const isNewItem = dataItem.isNew;

    const disableSave = (dataItem.DriverID === 0 && dataItem.VehicleID === 0) || dataItem.DeductionTypeID === 0 || dataItem.ScheduleType === 0 || dataItem.DeductionLineItemPayPeriodID === 0 ||
      (dataItem.DeductionLineItemAmount === 0 || dataItem.DeductionLineItemAmount == null) || (dataItem.ScheduleType === DeductionScheduleType.Amortized && dataItem.AmortizeOver < 1) ||
      (dataItem.TotalAmount > 0 && dataItem.DeductionLineItemAmount < 0) || (dataItem.TotalAmount < 0 && dataItem.DeductionLineItemAmount > 0);

    return inEdit ? (
      <td {..._props.tdProps} className={[_props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button themeColor={"primary"}
          disabled={disableSave}
          onClick={() =>
            isNewItem ? _props.add(dataItem) : _props.update(dataItem)
          }
        >
          {isNewItem ? "Save" : "Update"}
        </Button>
        <Button themeColor={"secondary"}
          onClick={() =>
            isNewItem ? _props.discard() : _props.cancel(dataItem)
          }
        >
          {isNewItem ? "Discard" : "Cancel"}
        </Button>
      </td>
    ) : (
      <td {..._props.tdProps} className={[_props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button themeColor={"primary"}
          disabled={scheduledDeductions.some(x => x.inEdit === true)}
          onClick={() => _props.edit(dataItem)}>Edit</Button>
        {/*<Button themeColor={"secondary"}*/}
        {/*    disabled={scheduledDeductions.some(x => x.inEdit === true)}*/}
        {/*    onClick={async () => {*/}
        {/*        if (!await confirm(`Remove ?`))*/}
        {/*            return;*/}
        {/*        _props.remove(dataItem);*/}
        {/*    }}>Delete</Button>*/}
      </td>
    );
  };

  const MyEditCommandCell = (props: GridCustomCellProps) => (
    <EditCommandCell {...props}
      edit={enterEdit}
      remove={remove}
      add={add}
      discard={discard}
      update={update}
      cancel={cancel}
      editField={editField}
    />
  );

  const enterEdit = (dataItem: PendingDeduction) => {
    if (scheduledDeductions.some(x => x.inEdit === true))
      return;

    setPendingDeductions(
      scheduledDeductions.map((item) =>
        item.PayDeductionLineItemID === dataItem.PayDeductionLineItemID ? { ...item, inEdit: true } : item
      )
    );
  };

  const remove = (dataItem: PendingDeduction) => {
    updateScheduledDeduction({ ...dataItem, Delete: true });
  };

  const add = (dataItem: PendingDeduction) => {
    updateScheduledDeduction(dataItem);
  };

  const update = (dataItem: PendingDeduction) => {
    updateScheduledDeduction(dataItem);
  };

  const cancel = (dataItem: PendingDeduction) => {
    const originalItem = CSIDataCopy.find(
      (p) => p.PayDeductionLineItemID === dataItem.PayDeductionLineItemID
    );
    const newData = scheduledDeductions.map((item) =>
      item.PayDeductionLineItemID === originalItem.PayDeductionLineItemID ? cloneObject(originalItem) : item
    );

    setPendingDeductions(newData);
  };

  const generateId = () =>
    scheduledDeductions.reduce((acc, current) => Math.max(acc, current.PayDeductionLineItemID), 0) + 1;

  const addNew = () => {
    const newDataItem = {
      inEdit: true, isNew: true, Delete: false,
      PayDeductionLineItemID: generateId(),
      AmortizeOver: 1,
      AmountPerPay: 0,
      DeductionLineItemComment: '',
      DeductionTypeID: 0,
      DriverID: 0,
      VehicleID: 0,
      DeductionLineItemPayPeriodID: 0,
      ScheduleType: 0,
      Status: DeductionLineItemStatusType.Pending,
      DeductionLineItemAmount: undefined,
      Paid: 0,
      Balance: 0,
      PaymentsRemaining: 0
    } as PendingDeduction;
    setPendingDeductions([newDataItem, ...scheduledDeductions]);
  };

  const discard = () => {
    const newData = [...scheduledDeductions];
    newData.splice(0, 1);
    setPendingDeductions(newData);
  };

  const SaveAndFetchPostDataState = (_dataState: DataSourceRequestState, _filterByOwnerID: number) => {
    setDataState(_dataState);

    setLoading(true);
    _requestHash.current = Math.random().toString(36).substring(7);
    debounceSearchInput(() => fetchRecords(false, _requestHash.current, _dataState, _filterByOwnerID));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearchInput = useCallback(
    debounce((func: () => void) => {
      func();
    }, 750), []);

  //const fetchRecords = useCallback((append: boolean, ownerId: number = 0) => {
  const fetchRecords = useCallback((append: boolean, requestHash: string, _dataState: DataSourceRequestState = null, _filterByOwnerID: number | null = null) => {
    setLoading(true);
    const dataGridState = _dataState == null ? { ...dataState } : { ..._dataState };
    if (!append) {
      dataGridState.take = PageSize;
      dataGridState.skip = 0;

      gridRef.current?.element?.querySelector<HTMLElement>('.k-grid-content')?.scrollTo(0, 0);
      setDataState(dataGridState);
    }

    //sessionStorage.setItem(SESSION_STORAGE_NAME, JSON.stringify(dataGridState.filter));

    let queryStr = `${toDataSourceRequestString(dataGridState)}`;
    if (_filterByOwnerID == null) {
      if (filterByOwnerId > 0) {
        queryStr += `&OwnerID=${filterByOwnerId}`
      }
    } else {
      if (_filterByOwnerID > 0) {
        queryStr += `&OwnerID=${_filterByOwnerID}`
      }
    }

    fetchApi(`/api/Settlements/GetPendingDeductions?${queryStr}`, {}, 'POST')
      .then(({ Data, Total }) => {
        if (_requestHash.current === requestHash) {
          setTotalRecords(Total);
          if (!append) {
            setPendingDeductions(Data);
            setCSIDataCopy(Data);
          } else {
            setPendingDeductions(prevData => prevData.concat(Data));
            setCSIDataCopy(prevData => prevData.concat(Data));
          }
        }
        setLoading(false);
        //  handleResize(null); //need this if using hierarchy filters
      }).catch(async e => {
        setLoading(false);
        if (!e?.status)
          await alert('Error: Please see admin');
        else if (e.status !== 404) {
          await alert(e?.detail);
        }
      });
  }, [alert, dataState, filterByOwnerId]);

  useEffect(() => {
    if (render.current === true) {
      render.current = false;
    } else {
      return;
    }

    _requestHash.current = Math.random().toString(36).substring(7);
    fetchRecords(false, _requestHash.current);
  }, [fetchRecords]);

  useEffect(() => {
    fetchApi('/api/settlements/getdeductiontypes')
      .then((response: any) => {
        setDeductionTypes((response.DeductionTypes as DeductionType[]).filter(x => x.Active === true));
      });
  }, []);

  //need this if using hierarchy filters
  //const handleResize = (e: UIEvent) => {
  //    if ((document.getElementsByClassName("k-grid")[0]) as HTMLDivElement != null) {
  //        ((document.getElementsByClassName("k-grid")[0]) as HTMLDivElement).style.height = (document.documentElement.clientHeight - 250) + "px";
  //    }
  //}

  const scrollHandler = (event: GridEvent) => {
    const scrollElement = event.syntheticEvent.currentTarget as HTMLElement;
    const isNearBottom = scrollElement.scrollHeight > scrollElement.clientHeight
      && scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10;

    if (!isNearBottom || loading || scheduledDeductions.length >= totalRecords)
      return;

    dataState.skip = scheduledDeductions.length;
    _requestHash.current = Math.random().toString(36).substring(7);
    fetchRecords(true, _requestHash.current);
  }

  const cloneObject = (obj: any): any => {
    return JSON.parse(JSON.stringify(obj));
  }

  const updateObject = (updatedObject: PendingDeduction, payDeductionLineItemID: number) => {
    setPendingDeductions(scheduledDeductions.map((obj) =>
      obj.PayDeductionLineItemID === payDeductionLineItemID ? updatedObject : obj
    ));

    setCSIDataCopy(CSIDataCopy.map((obj) =>
      obj.PayDeductionLineItemID === payDeductionLineItemID ? updatedObject : obj
    ));
  };

  const updateScheduledDeduction = (dataItem: PendingDeduction) => {
    setLoading(true);

    const data = { ...dataItem }
    fetchApi(`api/Settlements/PendingDeduction`, data, 'POST')
      .then(async (response: JsonResponse) => {
        if (response.Success === false) {
          setLoading(false);
          await alert(`Error: ${response.ErrorMessage}`);
        }
        else {
          data.isNew = false; data.inEdit = false;
          updateObject(data, data.PayDeductionLineItemID);
          setLoading(false);
        }
      })
      .catch(async e => {
        setLoading(false);
        // If not problem details
        if (!e?.status) await alert('An error occurred while saving.');
      });
  }


  const itemChange = (event: GridItemChangeEvent) => {
    const field = event.field || "";
    const newData = scheduledDeductions.map((item) =>
      item.PayDeductionLineItemID === event.dataItem.PayDeductionLineItemID
        ? { ...item, [field]: event.value }
        : item
    );
    setPendingDeductions(newData);
  }

  const removeFilter = (field: string) => {
    const hasFilter = (dataState.filter.filters as FilterDescriptor[]).some(x => x.field === field);
    if (hasFilter) {
      dataState.filter.filters = (dataState.filter.filters as FilterDescriptor[]).filter(x => x.field !== field);
      //setDataState({ ...dataState });
      SaveAndFetchPostDataState({ ...dataState }, filterByOwnerId);
    }
  }

  const updateOrAddFilter = (filterDescriptor: FilterDescriptor) => {
    const hasFilter = (dataState.filter.filters as FilterDescriptor[]).some(x => x.field === filterDescriptor.field);

    if (hasFilter) {
      const updatedFilters = (dataState.filter.filters as FilterDescriptor[]).map((obj) =>
        obj.field === filterDescriptor.field ? filterDescriptor : obj
      );
      //setDataState({ ...dataState, filter: { ...dataState.filter, filters: updatedFilters } });
      SaveAndFetchPostDataState({ ...dataState, filter: { ...dataState.filter, filters: updatedFilters } }, filterByOwnerId);
    } else {
      (dataState.filter.filters as FilterDescriptor[]).push(filterDescriptor);
      //setDataState({ ...dataState });
      SaveAndFetchPostDataState({ ...dataState }, filterByOwnerId);
    }
  };

  //const getTitle = (): JSX.Element => {

  //  let suffix: JSX.Element;
  //  if (payDeductionId > 0)
  //    suffix = <h5 style={{ display: 'inline-block', fontStyle: 'italic', fontWeight: 'bold' }}>(Filtered By Schedule Deduction ID)</h5>;
  //  else if (payPeriodId > 0)
  //    suffix = <h5 style={{ display: 'inline-block', fontStyle: 'italic', fontWeight: 'bold' }}>(Filtered By Pay Period ID)</h5>;

  //  return <h4>Pending Or Posted Pay Sheet Deductions {suffix ?? ''}</h4>;
  //}

  const getTitle = (): JSX.Element => {
    let suffix: JSX.Element;
    if (payDeductionId > 0)
      suffix = <span style={{ display: 'inline-block', fontStyle: 'italic', fontWeight: 'bold', fontSize: 'small' }}>(Filtered By Schedule Deduction ID {payDeductionId})</span>;

    return <h4>Pending Or Posted Pay Sheet Deductions {suffix ?? ''}</h4>;
  }

  const dataView = () => {
    return (<div className="mt-3" style={{ position: "relative" }}>
      <Title string="Pending Or Posted Pay Sheet Deductions" />
      {loading && <CenterDivPanel>
        <Loader type="converging-spinner" />
      </CenterDivPanel>}
      <div>{getTitle()}</div>
      <PendingDeductionsContext.Provider value={{ DeductionTypes: deductionTypes ?? [] }}>
        <Grid
          ref={gridRef}
          scrollable="scrollable"
          style={{
            maxHeight: '80vh',
          }}
          data={scheduledDeductions}
          {...dataState}
          onScroll={scrollHandler}
          resizable={true}
          onRowDoubleClick={(e: GridRowDoubleClickEvent) => enterEdit(e.dataItem)}
          onItemChange={itemChange}
          dataItemKey="PayDeductionLineItemID"
          edit={Object.fromEntries(scheduledDeductions.filter(x => x.inEdit).map(x => [x.PayDeductionLineItemID, true]))}
        >
          <GridToolbar>

            {payDeductionId == null && <>
              <DriverAutoComplete
                selectedDriverID={filterByDriverId}
                includeRecentlyRetiredDrivers
                style={{ width: '200px' }}
                onSelectedDriver={(e) => {
                  if (e?.DriverID > 0) {
                    updateOrAddFilter({ field: 'DriverID', operator: 'eq', value: e?.DriverID });
                  } else {
                    removeFilter('DriverID');
                  }
                  setFilterByDriverId(e?.DriverID);
                }}
              />

              <VehicleAutoComplete
                selectedVehicleID={filterByVehicleId}
                includeRecentlyRetiredVehicles
                style={{ width: '200px' }}
                onSelectedVehicle={(e) => {
                  if (e?.VehicleID > 0) {
                    updateOrAddFilter({ field: 'VehicleID', operator: 'eq', value: e?.VehicleID });
                  } else {
                    removeFilter('VehicleID');
                  }
                  setFilterByVehicleId(e?.VehicleID)
                }}
              />

              <OwnerAutoComplete
                selectedOwnerID={filterByOwnerId}
                style={{ width: '200px' }}
                onSelectedOwner={(e) => {
                  setFilterByOwnerId(e?.OwnerID ?? 0);
                  SaveAndFetchPostDataState(dataState, e?.OwnerID ?? 0);
                }}
              />

              <DropDownList
                style={{ width: '150px' }}
                data={[{ ID: 0, Name: 'Any Schedule' }].concat(deductionScheduleTypeData)}
                textField="Name"
                dataItemKey="ID"
                value={[{ ID: 0, Name: 'Any Schedule' }].concat(deductionScheduleTypeData).find((x: IDName) => x.ID == filterBySchedule)}
                onChange={(e) => {
                  setFilterBySchedule(e.target.value.ID);
                  if (e.target.value.ID > 0) {
                    updateOrAddFilter({ field: 'ScheduleType', operator: 'eq', value: e.target.value.ID });
                  } else {
                    removeFilter('ScheduleType');
                  }
                }}
              />

              <DropDownList
                style={{ width: '225px' }}
                data={[{ PayDeductionTypeID: 0, Name: 'Any Type' }].concat(deductionTypes)}
                textField="Name"
                dataItemKey="PayDeductionTypeID"
                value={[{ PayDeductionTypeID: 0, Name: 'Any Type' }].concat(deductionTypes).find((x) => x.PayDeductionTypeID == filterByDeductionType)}
                onChange={(e) => {
                  setFilterByDeductionType(e.target.value.PayDeductionTypeID);
                  if (e.target.value.PayDeductionTypeID > 0) {
                    updateOrAddFilter({ field: 'DeductionTypeID', operator: 'eq', value: e.target.value.PayDeductionTypeID });
                  } else {
                    removeFilter('DeductionTypeID');
                  }
                }}
              />
            </>}

            <DropDownList
              style={{ width: '150px' }}
              data={[{ ID: 0, Name: 'Any Status' }].concat(statusData)}
              //data={ statusData.unshift({ ID: 0, Name: 'Any' }) && statusData}
              textField="Name"
              dataItemKey="ID"
              value={[{ ID: 0, Name: 'Any Status' }].concat(statusData).find((x: IDName) => x.ID == filterByStatus)}
              onChange={(e) => {
                setFilterByStatus(e.target.value.ID);
                if (e.target.value.ID > 0) {
                  updateOrAddFilter({ field: 'DeductionLineItemPayStatus', operator: 'eq', value: e.target.value.ID });
                } else {
                  removeFilter('DeductionLineItemPayStatus');
                }

              }}
            />

            <PayPeriodDropDownList
              style={{ width: 250 }}
              onSelected={(e) => {
                setFilterByPayPeriod(e.PayPeriodID);
                if (e.PayPeriodID > 0) {
                  updateOrAddFilter({ field: 'DeductionLineItemPayPeriodID', operator: 'eq', value: e.PayPeriodID });
                } else {
                  removeFilter('DeductionLineItemPayPeriodID');
                }
              }}
              selectedPayPeriodID={filterByPayPeriod}
              includeClosedPayPeriods={true}
              includeOpenPayPeriods={true}
              defaultPayPeriodItem={{ PayPeriodID: 0, Label: 'Any Pay Period' }}
            />

            {/*{payPeriodId === 0 && <>*/}
            {/*  <NumericTextBox width={172} placeholder="Search By Schedule Ded. ID" spinners={false} step={0} format="#"*/}
            {/*    value={filterByPayDeductionId}*/}
            {/*    onChange={(e) => {*/}
            {/*      setFilterByPayDeductionId(e.value);*/}
            {/*      if (e.value > 0) {*/}
            {/*        updateOrAddFilter({ field: 'PayDeductionID', operator: 'eq', value: e.value });*/}
            {/*      } else {*/}
            {/*        removeFilter('PayDeductionID');*/}
            {/*      }*/}
            {/*    }} />*/}
            {/*</>}*/}

            <Button
              title="Clear All Filters and Sort"
              icon="filter-clear"
              svgIcon={filterClearIcon}
              onClick={() => {
                setFilterByDriverId(0);
                setFilterByVehicleId(0);
                setFilterByOwnerId(0);
                setFilterByStatus(0);
                setFilterBySchedule(0);
                setFilterByDeductionType(0);
                setFilterByPayPeriod(payPeriodId);
                setFilterByPayDeductionId(payDeductionId);

                //setDataState({ ...dataState, filter: { ...defaultAndFilter } });
                SaveAndFetchPostDataState({ ...dataState, filter: { ...defaultAndFilter } }, 0);
              }}
            />
          </GridToolbar>
          <Column field="PayDeductionLineItemID" title="ID" editable={false} cells={{ data: PayDeductionLineItemIDCell }} width={65} filterable={false} />
          <Column field="DriverID" title="Driver/Vehicle/Owner" cells={{ data: AssetCell, headerCell: AssetHeaderCell }} />
          <Column field="Amount" title="Amount" cells={{ data: AmountCell, headerCell: RightAlignHeaderCell }} filterable={false} />
          <Column field="DeductionLineItemComment" title="Comments" cells={{ data: CommentsCell }} filterable={false} />
          <Column field="Status" title="Status" cells={{ data: StatusCell }} width={125} filterable={false} />
          {/*need payperiod, deduction type and name*/}
          <Column field="ScheduleType" title="Schedule" cells={{ data: ScheduleCell, headerCell: ScheduleHeaderCell }} filterable={false} />
          <Column cells={{ data: MyEditCommandCell }} width={160} filterable={false} />
        </Grid>
      </PendingDeductionsContext.Provider>
      {
        totalRecords > 0 &&
        <div className="k-pager k-pager-md k-grid-pagert">
          <div style={{ marginLeft: "auto", marginRight: 0 }}>
            {formatNumber(Math.min(scheduledDeductions.length, totalRecords), 'n0')} of {formatNumber(totalRecords, 'n0')} items
          </div>
        </div>
      }
      <ConfirmationDialog />
    </div >);
  };

  if (props.onDialogClose) {
    return <KendoWindow
      autoSizePercentage={95}
      title="Generated/Posted Deductions"
      resizable
      onClose={props.onDialogClose}
    >
      {dataView()}
    </KendoWindow >
  }

  return dataView();
}

export default PendingDeductions;
